import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Lock, Loader2, LogOut, Mail, Building2, CalendarClock, RefreshCw } from "lucide-react";
import { api } from "../lib/api";

const TOKEN_KEY = "adonica_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [fetching, setFetching] = useState(false);

  const loadBookings = useCallback(
    async (t) => {
      setFetching(true);
      try {
        const { data } = await api.get("/admin/bookings", {
          headers: { Authorization: `Bearer ${t}` },
        });
        setBookings(data);
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
          toast.error("Session expired. Please sign in again.");
        } else {
          toast.error("Could not load inquiries.");
        }
      } finally {
        setFetching(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) loadBookings(token);
  }, [token, loadBookings]);

  const login = async (e) => {
    e.preventDefault();
    if (!passcode) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { passcode });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPasscode("");
      toast.success("Welcome back, Adonica.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Incorrect passcode.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setBookings([]);
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return iso;
    }
  };

  if (!token) {
    return (
      <div className="grain flex min-h-screen items-center justify-center bg-[#f4f2ec] px-6">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-md rounded-2xl border border-[var(--line)] bg-[#ffffff] p-10"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#9c7a33]/10 text-[#9c7a33]">
            <Lock className="h-5 w-5" />
          </div>
          <h1 className="mt-6 font-display text-4xl font-light text-[#1c2b4a]">
            Inquiry Dashboard
          </h1>
          <p className="mt-2 text-sm text-[#5c6474]">
            Enter your passcode to review speaking and booking requests.
          </p>
          <label className="mt-8 block">
            <span className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-[#5c6474]">
              Passcode
            </span>
            <input
              data-testid="admin-passcode-input"
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="••••••••"
              className="w-full border-0 border-b border-[var(--line)] bg-transparent pb-3 pt-1 text-[#1c2b4a] outline-none transition-colors focus:border-[#9c7a33] placeholder:text-[#1c2b4a]/25"
            />
          </label>
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#9c7a33] px-8 py-4 text-sm uppercase tracking-[0.16em] text-[#f4f2ec] transition-colors hover:bg-[#7e6128] disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enter"}
          </button>
          <a
            href="/"
            data-testid="admin-back-home"
            className="mt-6 block text-center text-xs uppercase tracking-[0.2em] text-[#5c6474] transition-colors hover:text-[#9c7a33]"
          >
            Back to site
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen bg-[#f4f2ec]">
      <header className="border-b border-[var(--line)] bg-[#f4f2ec]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-5 md:px-10">
          <a href="/" className="font-display text-2xl text-[#1c2b4a]">
            Adonica<span className="text-[#9c7a33]"> </span>Shaw
          </a>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={() => loadBookings(token)}
              className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] px-4 py-2 text-sm text-[#1c2b4a] transition-colors hover:border-[#9c7a33] hover:text-[#9c7a33]"
            >
              <RefreshCw className={`h-4 w-4 ${fetching ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-[#9c7a33] px-4 py-2 text-sm text-[#f4f2ec] transition-colors hover:bg-[#7e6128]"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-14 md:px-10">
        <div className="flex items-end justify-between">
          <div>
            <span className="text-[11px] uppercase tracking-[0.28em] text-[#9c7a33]">
              Booking & Speaking
            </span>
            <h1 className="mt-3 font-display text-4xl font-light text-[#1c2b4a] md:text-5xl">
              Inquiries
            </h1>
          </div>
          <span
            data-testid="admin-count"
            className="rounded-full border border-[var(--line)] px-4 py-2 text-sm text-[#5c6474]"
          >
            {bookings.length} total
          </span>
        </div>

        {fetching && bookings.length === 0 ? (
          <div className="mt-16 flex items-center gap-3 text-[#5c6474]">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading inquiries…
          </div>
        ) : bookings.length === 0 ? (
          <div
            data-testid="admin-empty"
            className="mt-16 rounded-2xl border border-dashed border-[var(--line)] p-16 text-center text-[#5c6474]"
          >
            No inquiries yet. New booking requests will appear here.
          </div>
        ) : (
          <div className="mt-10 grid gap-5">
            {bookings.map((b) => (
              <div
                key={b.id}
                data-testid="admin-booking-card"
                className="rounded-2xl border border-[var(--line)] bg-[#ffffff] p-7 md:p-8"
              >
                <div className="flex flex-col justify-between gap-2 border-b border-[var(--line)] pb-5 md:flex-row md:items-center">
                  <h3 className="font-display text-2xl text-[#1c2b4a]">{b.name}</h3>
                  <span className="text-xs uppercase tracking-[0.16em] text-[#5c6474]">
                    {fmtDate(b.created_at)}
                  </span>
                </div>
                <div className="mt-5 grid gap-4 text-sm text-[#5c6474] sm:grid-cols-2">
                  <a
                    href={`mailto:${b.email}`}
                    className="inline-flex items-center gap-2 transition-colors hover:text-[#9c7a33]"
                  >
                    <Mail className="h-4 w-4 text-[#9c7a33]" /> {b.email}
                  </a>
                  {b.organization ? (
                    <span className="inline-flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-[#9c7a33]" /> {b.organization}
                    </span>
                  ) : null}
                  {b.event_type ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="text-[#9c7a33]">Type:</span> {b.event_type}
                    </span>
                  ) : null}
                  {b.event_date ? (
                    <span className="inline-flex items-center gap-2">
                      <CalendarClock className="h-4 w-4 text-[#9c7a33]" /> {b.event_date}
                    </span>
                  ) : null}
                </div>
                <p className="mt-5 whitespace-pre-wrap leading-relaxed text-[#1c2b4a]">
                  {b.message}
                </p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
